/**
 * 📝 [Phase 103 — 2026-07-09] Liste des évaluations d'un employé (vue admin).
 *
 * Affiche l'historique + bouton "+ Nouvelle évaluation" (admin/owner uniquement).
 * Chaque item : année, période, note globale badge coloré, statut, actions.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Star, Plus, Eye, Loader2, ClipboardList } from "lucide-react";
import { EmployeeEvaluationForm } from "./EmployeeEvaluationForm";

const API_URL =
  (typeof window !== "undefined" && window.__API_URL__) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

function getAuthToken() {
  try {
    return (
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token") ||
      ""
    );
  } catch {
    return "";
  }
}
const authHeader = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function noteColor(note) {
  if (note == null) return "bg-slate-200 text-slate-500 border-slate-300";
  if (note < 2.5) return "bg-red-100 text-red-800 border-red-300";
  if (note < 3.5) return "bg-orange-100 text-orange-800 border-orange-300";
  if (note < 4) return "bg-yellow-100 text-yellow-800 border-yellow-300";
  return "bg-emerald-100 text-emerald-800 border-emerald-300";
}

const STATUT_LABELS = {
  brouillon: { label: "Brouillon", className: "bg-slate-100 text-slate-700 border-slate-300" },
  prete: { label: "Prête", className: "bg-amber-100 text-amber-800 border-amber-300" },
  publiee: { label: "Publiée", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  archivee: { label: "Archivée", className: "bg-slate-200 text-slate-600 border-slate-400" },
};

export function EmployeeEvaluationsList({
  employeeId,
  employeeName,
  canManage = true,
}) {
  const [loading, setLoading] = useState(true);
  const [evaluations, setEvaluations] = useState([]);
  const [config, setConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/employees/${employeeId}/evaluations`,
        { headers: authHeader() }
      );
      setEvaluations(res.data?.evaluations || []);
      setConfig(res.data?.config || null);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Erreur au chargement des évaluations"
      );
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId) load();
  }, [employeeId, load]);

  const handleNew = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = (evalDoc) => {
    setEditing(evalDoc);
    setShowForm(true);
  };

  const handleSaved = () => {
    load();
  };

  return (
    <div className="space-y-4" data-testid="evaluations-list">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-4 h-4 text-slate-500" />
            Historique des évaluations
          </h3>
          <p className="text-xs text-slate-500">
            {evaluations.length} évaluation{evaluations.length > 1 ? "s" : ""}
          </p>
        </div>
        {canManage && (
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={handleNew}
            data-testid="evaluations-new-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Nouvelle évaluation
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      ) : evaluations.length === 0 ? (
        <div
          className="p-6 border border-dashed border-slate-300 rounded-lg text-center text-sm text-slate-500 bg-slate-50"
          data-testid="evaluations-empty"
        >
          Aucune évaluation enregistrée pour cet employé.
          {canManage && (
            <>
              <br />
              <span className="text-xs">
                Cliquez sur « Nouvelle évaluation » pour commencer.
              </span>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {evaluations.map((e) => {
            const note =
              e.note_globale_manuelle != null
                ? e.note_globale_manuelle
                : e.note_globale_calculee;
            const stat = STATUT_LABELS[e.statut] || STATUT_LABELS.brouillon;
            return (
              <div
                key={e.id}
                className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors"
                data-testid={`evaluations-item-${e.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-slate-800">
                      Évaluation {e.annee}
                    </p>
                    <Badge
                      className={`text-xs border ${stat.className}`}
                      data-testid={`evaluations-statut-${e.id}`}
                    >
                      {stat.label}
                    </Badge>
                    {e.employee_acknowledged && (
                      <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                        Acquis par l&apos;employé
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Période : {e.periode_debut} → {e.periode_fin} · Éval. par{" "}
                    {e.evaluateur_nom || "?"}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge
                    className={`text-sm font-bold border ${noteColor(note)}`}
                    data-testid={`evaluations-note-${e.id}`}
                  >
                    <Star className="w-3 h-3 mr-1" />
                    {note != null ? `${note} / 5` : "—"}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(e)}
                    data-testid={`evaluations-open-${e.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Ouvrir
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <EmployeeEvaluationForm
        open={showForm}
        onOpenChange={setShowForm}
        employeeId={employeeId}
        employeeName={employeeName}
        initialEvaluation={editing}
        config={config}
        onSaved={handleSaved}
      />
    </div>
  );
}

export default EmployeeEvaluationsList;
